import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Calendar, Clock, ExternalLink, FileText, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { OPP_TYPES, OPP_STATUS_META } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function OpportunityDetail() {
  const { slug } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get(`/opportunities/${slug}`).then(({ data }) => setO(data)).catch(() => setError(true)); }, [slug]);
  useSeo({ title: o ? L(o, "title") : "", description: o ? L(o, "summary") : "", lang, image: o?.cover_image, type: "article" });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!o) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;
  const v = o.verification || {};
  const st = OPP_STATUS_META[o.status];

  const Row = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-champagne" /><span className="text-anthracite/50">{label} :</span><span className="text-anthracite/80">{value}</span></div>
  ) : null;

  return (
    <article className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
      <button onClick={() => navigate("/opportunites")} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne"><ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Toutes les opportunités" : "All opportunities"}</button>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.2em] text-champagne">{OPP_TYPES.find((x) => x.value === o.opp_type)?.[lang]}</span>
        <VerifiedBadge verified={o.rst_verified} size="lg" />
        {st && o.status === "updated" && <span className={`rounded-full border px-2.5 py-0.5 text-xs ${st.cls}`}>{st[lang]}</span>}
        {o.status === "expired" && <span className={`rounded-full border px-2.5 py-0.5 text-xs ${OPP_STATUS_META.expired.cls}`}>{OPP_STATUS_META.expired[lang]}</span>}
      </div>

      <h1 className="mb-4 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{L(o, "title")}</h1>
      <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(o, "summary")}</p>

      {o.cover_image && <div className="mb-10 aspect-[16/9] overflow-hidden bg-sand"><img src={o.cover_image} alt="" className="h-full w-full object-cover" /></div>}

      <div className="mb-8 grid gap-3 border-y border-tertiary py-5 sm:grid-cols-2">
        <Row icon={Tag} label={t("sector")} value={o.sector} />
        <Row icon={MapPin} label={lang === "fr" ? "Pays / marché" : "Country / market"} value={[o.region, o.country].filter(Boolean).join(", ")} />
        <Row icon={Sparkles} label={lang === "fr" ? "Montant" : "Amount"} value={o.amount_text} />
        <Row icon={Calendar} label={lang === "fr" ? "Date limite" : "Deadline"} value={o.deadline} />
      </div>

      {L(o, "description") && <Section title={lang === "fr" ? "Description" : "Description"} body={L(o, "description")} />}
      {L(o, "beneficiaries") && <Section title={lang === "fr" ? "Bénéficiaires" : "Beneficiaries"} body={L(o, "beneficiaries")} />}
      {L(o, "conditions") && <Section title={lang === "fr" ? "Conditions" : "Conditions"} body={L(o, "conditions")} />}

      {L(o, "rst_analysis") && (
        <div className="mb-8 border border-champagne/30 bg-champagne/5 p-6" data-testid="rst-analysis">
          <h3 className="mb-2 flex items-center gap-2 font-serif text-xl text-anthracite"><Sparkles className="h-4 w-4 text-champagne" /> {lang === "fr" ? "Analyse RSTI" : "RSTI Analysis"}</h3>
          <p className="whitespace-pre-line text-sm leading-relaxed text-anthracite/80">{L(o, "rst_analysis")}</p>
        </div>
      )}

      {o.documents?.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-3 font-serif text-xl text-anthracite">Documents</h3>
          <ul className="space-y-2">
            {o.documents.map((d, i) => (
              <li key={i}><a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-champagne hover:underline"><FileText className="h-4 w-4" /> {d.name || d.url}</a></li>
            ))}
          </ul>
        </div>
      )}

      <p className="mb-10 text-xs text-anthracite/50" data-testid="rst-trust-note">{lang === "fr" ? "Les fiches publiées ont fait l'objet d'une validation interne par RSTI. Cette validation réduit l'incertitude mais ne constitue jamais une garantie d'absence de risque." : "Published records undergo internal validation by RSTI. This reduces uncertainty but is never a guarantee of the absence of risk."}</p>

      <div className="flex flex-col gap-3 border-t border-tertiary pt-8 sm:flex-row">
        <Link to={`/connect?category=opportunities&ref=${encodeURIComponent(L(o, "title"))}`} data-testid="cta-interested" className="flex-1 bg-anthracite px-6 py-4 text-center text-sm font-medium text-ivory transition-colors hover:bg-black">{lang === "fr" ? "Je suis intéressé" : "I'm interested"}</Link>
        <Link to={`/connect?category=opportunities&ref=${encodeURIComponent(L(o, "title"))}`} data-testid="cta-accompanied" className="flex-1 border border-anthracite px-6 py-4 text-center text-sm font-medium text-anthracite transition-colors hover:bg-anthracite hover:text-ivory">{lang === "fr" ? "Être accompagné par RSTI" : "Get RSTI support"}</Link>
      </div>
    </article>
  );
}

function Section({ title, body }) {
  return (
    <div className="mb-8">
      <h3 className="mb-2 font-serif text-xl text-anthracite">{title}</h3>
      <p className="whitespace-pre-line text-base leading-relaxed text-anthracite/80">{body}</p>
    </div>
  );
}
